import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase/config";
import type {
  CreateSlideshowPlanRequest,
  SlideshowApiResponse,
  SlideshowJob,
  StartSlideshowGenerationRequest,
} from "../types/slideshow";

const COLLECTION = "slideshowJobs";
const CREATE_ENDPOINT =
  process.env.REACT_APP_CREATE_SLIDESHOW_PLAN_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/createSlideshowPlan";
const START_ENDPOINT =
  process.env.REACT_APP_START_SLIDESHOW_GENERATION_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/startSlideshowGeneration";

class SlideshowService {
  private toIso(value: any): string {
    if (value instanceof Timestamp) return value.toDate().toISOString();
    if (value?.toDate && typeof value.toDate === "function") {
      return value.toDate().toISOString();
    }
    const parsed = new Date(value || Date.now());
    return Number.isNaN(parsed.getTime())
      ? new Date().toISOString()
      : parsed.toISOString();
  }

  private async post<T>(url: string, body: Record<string, unknown>): Promise<T> {
    await auth.authStateReady();
    const user = auth.currentUser;
    if (!user) throw new Error("Authentication required.");
    const idToken = await user.getIdToken();
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });
    const text = await response.text();
    let data: any = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }
    if (!response.ok) {
      throw new Error(
        data?.error || data?.message || text || `Request failed (${response.status})`,
      );
    }
    return data as T;
  }

  private parseJob(id: string, data: Record<string, any>): SlideshowJob {
    return {
      jobId: String(data.jobId || id),
      appId: String(data.appId || ""),
      appName: String(data.appName || ""),
      requesterUserId: String(data.requesterUserId || ""),
      status:
        data.status === "review_pending" ||
        data.status === "queued" ||
        data.status === "processing" ||
        data.status === "completed" ||
        data.status === "failed"
          ? data.status
          : "review_pending",
      phase:
        data.phase === "review" ||
        data.phase === "generation" ||
        data.phase === "completed" ||
        data.phase === "failed"
          ? data.phase
          : "review",
      inputs: {
        objective: String(data.inputs?.objective || ""),
        viralPlaybook: String(data.inputs?.viralPlaybook || ""),
        upsellPlaybook: String(data.inputs?.upsellPlaybook || ""),
        slideCount: Number(data.inputs?.slideCount || 5),
        creativeDirection: String(data.inputs?.creativeDirection || ""),
      },
      planning:
        data.planning && typeof data.planning === "object" ? data.planning : undefined,
      slideAssets: Array.isArray(data.slideAssets) ? data.slideAssets : undefined,
      zipAsset:
        data.zipAsset && typeof data.zipAsset === "object" ? data.zipAsset : undefined,
      captionsByPlatform:
        data.captionsByPlatform && typeof data.captionsByPlatform === "object"
          ? data.captionsByPlatform
          : undefined,
      progress: {
        stage: String(data.progress?.stage || "review_pending") as any,
        message: String(data.progress?.message || ""),
        percent: Math.max(0, Math.min(100, Number(data.progress?.percent || 0))),
      },
      report: data.report && typeof data.report === "object" ? data.report : undefined,
      error: data.error ? String(data.error) : undefined,
      createdAt: this.toIso(data.createdAt),
      updatedAt: this.toIso(data.updatedAt),
    };
  }

  async createPlan(payload: CreateSlideshowPlanRequest): Promise<SlideshowApiResponse> {
    return this.post<SlideshowApiResponse>(
      CREATE_ENDPOINT,
      payload as unknown as Record<string, unknown>,
    );
  }

  async startGeneration(
    payload: StartSlideshowGenerationRequest,
  ): Promise<SlideshowApiResponse> {
    return this.post<SlideshowApiResponse>(
      START_ENDPOINT,
      payload as unknown as Record<string, unknown>,
    );
  }

  async listJobs(params?: { appId?: string }): Promise<SlideshowJob[]> {
    await auth.authStateReady();
    const user = auth.currentUser;
    const requesterUserId = user?.email || user?.uid || "";
    if (!requesterUserId) return [];
    const constraints: any[] = [where("requesterUserId", "==", requesterUserId)];
    if (params?.appId) constraints.push(where("appId", "==", params.appId));
    const snapshot = await getDocs(query(collection(db, COLLECTION), ...constraints));
    return snapshot.docs
      .map((docSnap) => this.parseJob(docSnap.id, docSnap.data()))
      .sort(
        (left, right) =>
          new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
      );
  }

  async getJob(jobId: string): Promise<SlideshowJob | null> {
    const snap = await getDoc(doc(db, COLLECTION, jobId));
    if (!snap.exists()) return null;
    return this.parseJob(snap.id, snap.data());
  }
}

const slideshowService = new SlideshowService();
export default slideshowService;
